import { commentEditWindowOpen, withdrawnCommentLabel } from "@/api/caseTable/caseTableDefinition";
import type { EntryComment, MentionableMember } from "@/api/comments/CommentsApi";
import {
  useAddEntryComment,
  useEditEntryComment,
  useWithdrawEntryComment,
} from "@/api/comments/useComments";
import { useAuth } from "@/auth/AuthContext";
import { MEMBERSHIP_ROLE_LABEL, labelOf } from "@/constants/uiLabels";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

function roleBadges(roles: string[] | null | undefined): string {
  return (roles ?? [])
    .map((r) => labelOf(MEMBERSHIP_ROLE_LABEL, r, ""))
    .filter(Boolean)
    .join(" · ");
}

function mentionedIdsFromBody(
  body: string,
  members: MentionableMember[]
): string[] {
  const lower = body.toLowerCase();
  return members
    .filter((m) => {
      const name = (m.display_name ?? "").trim();
      if (!name) return false;
      return lower.includes("@" + name.toLowerCase());
    })
    .map((m) => m.profile_id);
}

export function EntryComments({
  caseId,
  entryId,
  comments,
  members,
  isOrgAdmin = false,
}: {
  caseId: string;
  entryId: string;
  comments: EntryComment[];
  members: MentionableMember[];
  isOrgAdmin?: boolean;
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const add = useAddEntryComment(caseId);
  const edit = useEditEntryComment(caseId);
  const withdraw = useWithdrawEntryComment(caseId);
  const [body, setBody] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const mentionQuery = useMemo(() => {
    const at = body.lastIndexOf("@");
    if (at < 0) return "";
    return body.slice(at + 1).trim().toLowerCase();
  }, [body]);

  const suggestions = useMemo(() => {
    if (!body.includes("@")) return [];
    const q = mentionQuery;
    return members
      .filter((m) =>
        (m.display_name ?? "").toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [body, members, mentionQuery]);

  const pickMention = (member: MentionableMember) => {
    const at = body.lastIndexOf("@");
    const prefix = at >= 0 ? body.slice(0, at) : body;
    const name = member.display_name ?? "Mitglied";
    setBody(`${prefix}@${name} `);
  };

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setMessage(null);
    try {
      await add.mutateAsync({
        entryId,
        body: text,
        mentionedProfileIds: mentionedIdsFromBody(text, members),
      });
      setBody("");
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  const saveEdit = async (comment: EntryComment) => {
    const text = editBody.trim();
    if (!text) return;
    setMessage(null);
    try {
      await edit.mutateAsync({
        commentId: comment.id,
        body: text,
        mentionedProfileIds: mentionedIdsFromBody(text, members),
      });
      setEditingId(null);
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  return (
    <View style={{ gap: 6, marginTop: 6 }}>
      {comments.map((c) => {
        const withdrawn = !!c.withdrawn_at;
        const canEdit =
          !withdrawn &&
          c.author_id === user?.id &&
          commentEditWindowOpen(c.created_at, Date.now());
        const canWithdraw =
          !withdrawn && (c.author_id === user?.id || isOrgAdmin);
        return (
          <View
            key={c.id}
            style={{
              paddingTop: 6,
              borderTopWidth: 1,
              borderTopColor: theme.color.border,
              gap: 2,
            }}
          >
            <Text style={{ color: theme.color.muted, fontSize: 12 }}>
              {c.author_display_name ?? "Mitglied"}
              {roleBadges(c.author_roles)
                ? ` · ${roleBadges(c.author_roles)}`
                : ""}
              {c.edited_at && !withdrawn ? " · bearbeitet" : ""}
            </Text>
            {editingId === c.id ? (
              <>
                <FloatingLabelInput
                  label="Kommentar"
                  value={editBody}
                  onChangeText={setEditBody}
                />
                <View style={{ flexDirection: "row", gap: 8 }}>
                  <AppButton onPress={() => void saveEdit(c)}>Speichern</AppButton>
                  <AppButton variant="secondary" onPress={() => setEditingId(null)}>
                    Abbrechen
                  </AppButton>
                </View>
              </>
            ) : (
              <Text
                style={{
                  color: withdrawn ? theme.color.muted : theme.color.text,
                  fontSize: 13,
                  fontStyle: withdrawn ? "italic" : "normal",
                }}
              >
                {withdrawnCommentLabel(c)}
              </Text>
            )}
            {!withdrawn && editingId !== c.id ? (
              <View style={{ flexDirection: "row", gap: 12 }}>
                {canEdit ? (
                  <Pressable
                    onPress={() => {
                      setEditingId(c.id);
                      setEditBody(c.body ?? "");
                    }}
                  >
                    <Text style={{ color: theme.color.primary, fontSize: 12 }}>
                      Bearbeiten
                    </Text>
                  </Pressable>
                ) : null}
                {canWithdraw ? (
                  <Pressable onPress={() => void withdraw.mutateAsync(c.id)}>
                    <Text style={{ color: theme.color.danger, fontSize: 12 }}>
                      Zurückziehen
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </View>
        );
      })}

      <FloatingLabelInput
        label="Kommentar"
        value={body}
        onChangeText={setBody}
      />
      {suggestions.length > 0 ? (
        <View style={{ gap: 4 }}>
          {suggestions.map((m) => (
            <Pressable key={m.profile_id} onPress={() => pickMention(m)}>
              <Text style={{ color: theme.color.primary, fontSize: 13 }}>
                @{m.display_name}
                {roleBadges(m.roles) ? ` · ${roleBadges(m.roles)}` : ""}
              </Text>
            </Pressable>
          ))}
        </View>
      ) : null}
      <AppButton onPress={() => void submit()} disabled={add.isPending}>
        Kommentieren
      </AppButton>
      {message ? (
        <Text style={{ color: theme.color.danger, fontSize: 12 }}>{message}</Text>
      ) : null}
    </View>
  );
}
